import { useMutation, useQuery } from '@apollo/client';
import { Empty, Spin, Tag, notification } from 'antd';
import React, { useMemo } from 'react';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import {
  PartnerOnboardingStages,
  extractMyOnboardingTasks,
  myOnboardingTasksQuery,
  submitOnboardingTaskResponseMutation,
} from './constants';
import { PartnerGroup } from './MyOnboardingTaskRow';

function MyOnboardingTasksPage() {
  const { data, loading, refetch } = useQuery(myOnboardingTasksQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const tasks = extractMyOnboardingTasks(data);

  const [submitResponse] = useMutation(submitOnboardingTaskResponseMutation);

  const handleSubmit = async (partnerOnboardingId, templateId, answer) => {
    try {
      await submitResponse({
        variables: { input: { _id: partnerOnboardingId, templateId, answer } },
      });
      await refetch();
    } catch (err) {
      notification.error({
        message: 'Error saving task',
        description: err.message,
      });
    }
  };

  // Backend returns tasks pre-sorted by (stage, contactName, taskName). Partition into
  // stage sections (ascending), and within each stage group by partner. A partner with
  // tasks in multiple stages appears under each — matches "ordered by stage ascending".
  const stageSections = useMemo(() => {
    const stages = new Map();
    tasks.forEach((task) => {
      if (!stages.has(task.stageNumber))
        stages.set(task.stageNumber, new Map());
      const partners = stages.get(task.stageNumber);
      if (!partners.has(task.partnerOnboardingId)) {
        partners.set(task.partnerOnboardingId, {
          partnerOnboardingId: task.partnerOnboardingId,
          contactId: task.contactId,
          contactName: task.contactName,
          region: task.region,
          regionName: task.regionName,
          tasks: [],
        });
      }
      partners.get(task.partnerOnboardingId).tasks.push(task);
    });
    return [...stages.keys()]
      .sort((a, b) => a - b)
      .map((stage) => ({ stage, groups: [...stages.get(stage).values()] }));
  }, [tasks]);

  return (
    <CSPage title="My Partner Onboarding Tasks">
      <CSPageHeader title="My Partner Onboarding Tasks" />
      {loading && !tasks.length && <Spin />}
      {!loading && !tasks.length && (
        <Empty description="No onboarding tasks are currently assigned to you" />
      )}
      {stageSections.map(({ stage, groups }) => {
        const stageMeta = PartnerOnboardingStages[stage];
        return (
          <div key={stage} style={{ marginBottom: 32 }}>
            <div style={{ marginBottom: 12 }}>
              <Tag color={stageMeta?.color}>
                {`Stage ${stage} — ${stageMeta?.label || stage}`}
              </Tag>
            </div>
            {groups.map((group) => (
              <PartnerGroup
                key={group.partnerOnboardingId}
                {...group}
                onSubmit={handleSubmit}
              />
            ))}
          </div>
        );
      })}
    </CSPage>
  );
}

export default MyOnboardingTasksPage;
